import React from 'react';
import { AddBoxOutlined, DescriptionOutlined, Event, PermContactCalendar } from '@mui/icons-material';
import { ProviderMedicalRole } from '@uplift/types';
import { useCurrentuser } from '@uplift/contexts';
import { useAppointmentByIdQuery, usePatientNextAppointmentsQuery } from '@uplift/queries';
import { CalendarDateService } from '@uplift/services';
import { VideoCallDrawerContentContainer, VideoCallSidebar, VideoCallSidebarAlertProvider, VideoCallSidebarItem, useVideoCallSidebarContext } from '../Layout';
import { ProfileDrawer } from './ProfileDrawer.component';
import { InCallScheduler } from './InCallScheduler';
import { FollowUpSchedulerProvider } from '@uplift/ui-v2';
import { useVideoContext } from '../../../hooks';
import { SidebarButtonType } from '@uplift/api';


export type ProviderSidebarProps = {
  appointmentId: string;
  patientId: string;
  medicalRole: ProviderMedicalRole | null;
  sessionId: string;
  renderPsychiatryDrawer?: VideoCallSidebarItem['renderContent'];
  SessionNoteComponent?: React.ComponentType<{ sessionId: string; patientId: string }>;
};


export const ProviderSidebar: React.VFC<ProviderSidebarProps> = ({ SessionNoteComponent, ...props }) => {
  const me = useCurrentuser();
  const { isSharingScreen } = useVideoContext();
  const { activeItemId, setActiveItemId } = useVideoCallSidebarContext();
  const [ followUpDate, setFollowUpDate ] = React.useState<string | undefined>(undefined);
  const { data: currentAppointment } = useAppointmentByIdQuery(props.appointmentId);

  const { data: upcomingAppointments } = usePatientNextAppointmentsQuery({
    patient_id: props.patientId,
    provider_id: me.id,
    dt_from: currentAppointment ? CalendarDateService.date(currentAppointment.fromDatetime).add(1, 'second').format() : undefined,
    appointments_per_series: 1,
    limit: 1,
  }, { enabled: !!currentAppointment });
  const scheduledFollowUpDate = upcomingAppointments?.[0]?.fromDatetime ?? null;

  const [ skipToSessionDetails, setSkipToSessionDetails ] = React.useState(false);

  const openFollowUpScheduler = React.useCallback((suggestedDate: string) => {
    setFollowUpDate(suggestedDate);
    setSkipToSessionDetails(false);
    setActiveItemId('scheduling');
  }, [ setActiveItemId ]);

  const openFollowUpSessionDetails = React.useCallback((suggestedDate: string) => {
    setFollowUpDate(suggestedDate);
    setSkipToSessionDetails(true);
    setActiveItemId('scheduling');
  }, [ setActiveItemId ]);

  React.useEffect(() => {
    if (activeItemId === 'scheduling') {
      return;
    }

    setFollowUpDate(undefined);
    setSkipToSessionDetails(false);
  }, [ activeItemId ]);

  const sidebarItems: VideoCallSidebarItem[] = React.useMemo(() => {
    const sidebarItems: VideoCallSidebarItem[] = [];

    if (props.medicalRole === ProviderMedicalRole.psychiatrist && SessionNoteComponent) {
      sidebarItems.push({
        icon: <DescriptionOutlined />,
        renderContent: (onClose) => {
          return (
            <VideoCallDrawerContentContainer onClose={onClose} title="Session Notes">
              <FollowUpSchedulerProvider
                onOpen={openFollowUpScheduler}
                onOpenSessionDetails={openFollowUpSessionDetails}
                scheduledFollowUpDate={scheduledFollowUpDate}
              >
                <SessionNoteComponent
                  sessionId={props.sessionId}
                  patientId={props.patientId}
                />
              </FollowUpSchedulerProvider>
            </VideoCallDrawerContentContainer>
          );
        },
        tooltipTitle: SidebarButtonType.notes,
        id: 'notes',
        keepMounted: true,
        contentWidth: 554,
      });
    }

    sidebarItems.push({
      icon: <PermContactCalendar />,
      renderContent: (onClose) => <ProfileDrawer onClose={onClose} patientId={props.patientId} medicalRole={props.medicalRole} />,
      tooltipTitle: SidebarButtonType.profile,
      id: 'profile',
      keepMounted: true,
      contentWidth: 440
    });

    if (props.medicalRole === ProviderMedicalRole.therapist && props.renderPsychiatryDrawer) {
      sidebarItems.push({
        icon: <AddBoxOutlined />,
        renderContent: props.renderPsychiatryDrawer,
        // TODO: this will be false once we can fix the rendering issue with gqty not re-rendering based on new data
        keepMounted: false,
        tooltipTitle: SidebarButtonType.refer,
        id: 'refer',
        contentWidth: 440
      });
    }

    sidebarItems.push({
      icon: <Event />,
      renderContent: (onClose) => {
        return (
          <VideoCallSidebarAlertProvider contentWidth="440px">
            <InCallScheduler
              patientId={props.patientId}
              appointmentId={props.appointmentId}
              initialDate={followUpDate}
              skipToSessionDetails={skipToSessionDetails}
              onClose={onClose}
            />
          </VideoCallSidebarAlertProvider>
        );
      },
      tooltipTitle: SidebarButtonType.scheduling,
      id: 'scheduling',
      contentWidth: 440,
      disabled: isSharingScreen,
    });

    return sidebarItems;
  }, [ SessionNoteComponent, props.medicalRole, props.patientId, props.renderPsychiatryDrawer, props.sessionId, props.appointmentId, isSharingScreen, openFollowUpScheduler, openFollowUpSessionDetails, followUpDate, skipToSessionDetails, scheduledFollowUpDate ]);


  return <VideoCallSidebar sidebarItems={sidebarItems} />;
};
